// Keyboard events go to the focused document. An App's frame is cross-origin,
// so a keyboard App (a game on Space, arrow-key navigation) heard nothing until
// the person clicked inside it, and the App cannot take focus itself without a
// gesture. When the App is ready, the host gives its frame focus, unless the
// person is typing in another field in Chat (the composer, a dialog).
function editing(element) {
  return Boolean(
    element &&
    (element.isContentEditable === true ||
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(String(element.tagName).toUpperCase())),
  );
}

/** Focus the App frame unless the person is typing elsewhere. Returns whether it did. */
export function focusReadyFrame(iframe, document) {
  if (typeof iframe?.focus !== 'function') return false;
  const active = document?.activeElement ?? null;
  if (active !== iframe && editing(active)) return false;
  iframe.focus({ preventScroll: true });
  return true;
}

/** Calls `done` once `ready()` holds after an `inert` change anywhere in the document, or stops after `timeoutMs`. */
function whenInertClears(document, ready, done, timeoutMs) {
  const view = document?.defaultView;
  const root = document?.documentElement;
  if (!view?.MutationObserver || !root) return () => {};
  let finished = false;
  const finish = (shown) => {
    if (finished) return;
    finished = true;
    observer.disconnect();
    view.clearTimeout(timer);
    if (shown) done();
  };
  const observer = new view.MutationObserver(() => {
    if (ready()) finish(true);
  });
  observer.observe(root, { attributes: true, attributeFilter: ['inert'], subtree: true });
  const timer = view.setTimeout(() => finish(false), timeoutMs);
  return () => finish(false);
}

/**
 * Focus the frame once it is on show. A rebuild's frame waits inert behind the App on show until it replaces it,
 * and an inert frame cannot take focus, so the focus waits (at most `timeoutMs`) for the inert to clear, then
 * applies the same rule: never while the person is typing elsewhere, and only while `still()` holds. Returns a
 * function that stops waiting.
 */
export function focusFrameWhenShown(
  iframe,
  document,
  { still = () => true, timeoutMs = 2000 } = {},
) {
  const shown = () => !iframe?.closest?.('[inert]');
  const focus = () => {
    if (iframe?.isConnected !== false && still()) focusReadyFrame(iframe, document);
  };
  if (!shown()) return whenInertClears(document, shown, focus, timeoutMs);
  focus();
  return () => {};
}
